import {
  FORMAT_TYPES,
  TAG_TYPES,
  CUSTOM_BLOCK_TYPES,
  createElement,
  setAttribute,
  createTextNode,
  BLOCKLEVELTYPES,
  ASSIST_GROUP_TYPE,
  INLINETYPES,
  isHeaderType
} from "../index.js";

/**
 * Convert a rich text value node to HTML.
 *
 * This function takes a rich text value node and converts its content to HTML
 * format, preserving the formatting and structure of the original content.
 *
 * @param {Object} richTextNode - A JavaScript object representing a rich text value node,
 *                                typically obtained from a structured data source.
 *
 * @returns {string} The HTML representation of the rich text content.
 */
export function deserializeNode(json = [], readOnly = false) {
  if (json.length === 0) return "";
  let htmlNodes = [];
  json.forEach((item) => {
    if (BLOCKLEVELTYPES.includes(item.type)) {
      const blockType = item.type;
      const isHeader = isHeaderType(blockType);
      if (isHeader) {
        const element = createElement(blockType);
        if (item.data?.align) {
          const align = item.data.align.toLowerCase();
          setAttribute(element, "data-align", align);
          setAttribute(element, "class", `ql-align-${align}`);
        }
        deserializeInlineNodes(item.nodes, item, element);
        htmlNodes.push(element);
      } else if (
        [
          FORMAT_TYPES.OL_LIST,
          FORMAT_TYPES.UL_LIST,
          FORMAT_TYPES.CODE,
          FORMAT_TYPES.BLOCKQUOTE
        ].includes(item.type)
      ) {
        const grpElement = createElement(ASSIST_GROUP_TYPE[item.type]);
        setAttribute(grpElement, "class", `ql-${blockType}`);
        deserializeGroupNodes(item.nodes, item, grpElement);
        htmlNodes.push(grpElement);
      }
    } else if (item.type === FORMAT_TYPES.PARAGRAPH) {
      /** Check if code blocks exists inside paragraph */
      if (item.nodes[0] && item.nodes[0]?.type === FORMAT_TYPES.CODE) {
        const node = item.nodes[0];
        const grpElement = createElement(ASSIST_GROUP_TYPE[node.type]);
        setAttribute(grpElement, "class", `ql-${node.type}`);
        deserializeGroupNodes(node.nodes, node, grpElement);
        htmlNodes.push(grpElement);
      } else {
        const element = createElement(item.type);
        if (item.data?.align) {
          const align = item.data.align.toLowerCase();
          setAttribute(element, "data-align", align);
          setAttribute(element, "class", `ql-align-${align}`);
        }
        deserializeInlineNodes(item.nodes, item, element);
        htmlNodes.push(element);
      }
    } else if (CUSTOM_BLOCK_TYPES.includes(item.type)) {
      /** serialization for custom block components*/
      const type = item.type;
      const data = item.data;
      const element = createElement("div");
      setAttribute(element, "class", `ql-${type}`);
      setAttribute(element, "data-type", type);
      setAttribute(element, `data-${type}`, JSON.stringify(data ? data : {}));
      htmlNodes.push(element);
    }
  });
  const htmlstr = htmlNodes.reduce((str, node) => (str += node.outerHTML), "");
  return htmlstr;
}

function deserializeGroupNodes(nodes = [], parentNode, grpElement) {
  nodes.length > 0 &&
    nodes.forEach((node) => {
      const element = createElement(parentNode.type);
      setAttribute(element, "class", `ql-${parentNode.type}-item`);
      deserializeInlineNodes(node.nodes, node, element);
      if (node.data?.align) {
        const align = node.data.align.toLowerCase();
        setAttribute(element, "data-align", align);
        setAttribute(element, "class", `ql-align-${align}`);
      }
      grpElement.appendChild(element);
    });
}

function deserializeInlineNodes(nodes = [], parentNode, parentHTMLNode) {
  nodes.length > 0 &&
    nodes.forEach((node) => {
      if (node.type === FORMAT_TYPES.PARAGRAPH) {
        if (node.data?.align) {
          const align = node.data.align.toLowerCase();
          setAttribute(parentHTMLNode, "data-align", align);
          setAttribute(parentHTMLNode, "class", `ql-align-${align}`);
        }
        deserializeInlineNodes(node.nodes, parentNode, parentHTMLNode);
      } else {
        deserializeNodes([node], parentNode, parentHTMLNode);
      }
    });
}

function deserializeNodes(nodes = [], parentNode, parentHTMLNode) {
  nodes.length > 0 &&
    nodes.forEach((node) => {
      if (INLINETYPES.includes(node.type)) {
        if (node.type === FORMAT_TYPES.COLOR) {
          deserializeNodes(node.nodes, node, parentHTMLNode);
          return;
        }
        const currNode = createElement(node.type);
        if (node.type === FORMAT_TYPES.LINK) {
          const { href, text } = node.data;
          setAttribute(currNode, "href", href);
          setAttribute(currNode, "class", "ql-link");
          setAttribute(currNode, "target", "_blank");
          setAttribute(
            currNode,
            `data-${node.type}`,
            JSON.stringify({ href, text })
          );
        }
        if (parentNode.type === FORMAT_TYPES.COLOR) {
          if (parentHTMLNode.nodeName.toLowerCase() === TAG_TYPES.A) {
            setAttribute(
              parentHTMLNode,
              "style",
              `color: ${parentNode.data.color}`
            );
          } else {
            setAttribute(currNode, "style", `color: ${parentNode.data.color}`);
          }
        }
        parentHTMLNode.appendChild(currNode);
        deserializeNodes(
          node.nodes,
          node,
          parentHTMLNode.childNodes[parentHTMLNode.childNodes.length - 1]
        );
      } else if (node.type === TAG_TYPES.TEXT) {
        const textNode = createTextNode(node.text || "");
        if (
          parentNode.type === FORMAT_TYPES.COLOR &&
          parentHTMLNode.nodeName.toLowerCase() !== TAG_TYPES.A
        ) {
          const spanElement = createElement("span");
          setAttribute(spanElement, "style", `color: ${parentNode.data.color}`);
          spanElement.appendChild(textNode);
          parentHTMLNode.appendChild(spanElement);
        } else {
          parentHTMLNode.appendChild(textNode);
        }
      } else {
        const currNode = createElement("span");
        setAttribute(currNode, "class", `ql-${node.type}`);
        setAttribute(currNode, "data-type", node.type);
        setAttribute(
          currNode,
          `data-${node.type}`,
          JSON.stringify(node.data ? node.data : {})
        );
        deserializeNodes(node.nodes, node, currNode);
        if (
          parentNode.type === FORMAT_TYPES.COLOR &&
          parentHTMLNode.nodeName !== TAG_TYPES.A
        ) {
          const spanElement = createElement("span");
          setAttribute(spanElement, "style", `color: ${parentNode.data.color}`);
          spanElement.appendChild(currNode);
          parentHTMLNode.appendChild(spanElement);
        } else {
          parentHTMLNode.appendChild(currNode);
        }
      }
    });
}
